<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowRight, BrainCircuit, GitBranch, Network, RadioTower, UsersRound } from 'lucide-vue-next'

import ExpandableText from '@/components/ExpandableText.vue'
import type {
  IntelligenceEventContext,
  IntelligenceEventRelation,
  IntelligenceGraph,
  MarketImpactEvidence,
} from '@/types/monitor'
import { formatLocalDateTime } from '@/utils/format'

const props = defineProps<{
  graph: IntelligenceGraph | null
  loading: boolean
}>()

const { t } = useI18n()
const relationFilter = ref('all')

const events = computed(() => props.graph?.events ?? [])
const relations = computed(() => props.graph?.relations ?? [])
const marketImpacts = computed(() => props.graph?.market_impacts ?? [])
const eventById = computed(() => new Map(events.value.map((context) => [context.event.id, context])))
const marketImpactByEvent = computed(() => {
  const grouped = new Map<string, MarketImpactEvidence[]>()
  for (const evidence of marketImpacts.value) {
    const items = grouped.get(evidence.event_id) ?? []
    items.push(evidence)
    grouped.set(evidence.event_id, items)
  }
  for (const items of grouped.values()) items.sort((left, right) => left.hypothesis_index - right.hypothesis_index)
  return grouped
})
const sourceCount = computed(() => {
  const sources = new Set(
    events.value.map((context) => context.channel_username || context.channel_name).filter(Boolean),
  )
  return sources.size
})
const entityCount = computed(() => {
  const entities = new Set<string>()
  for (const context of events.value) {
    for (const entity of context.event.entities) entities.add(entity.name.toLocaleLowerCase())
    if (context.event.speaker_name) entities.add(context.event.speaker_name.toLocaleLowerCase())
  }
  return entities.size
})
const relationTypes = computed(() => {
  const counts = new Map<string, number>()
  for (const relation of relations.value) {
    counts.set(relation.relation_type, (counts.get(relation.relation_type) ?? 0) + 1)
  }
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1])
})
const filteredRelations = computed(() =>
  relations.value
    .filter((relation) => relationFilter.value === 'all' || relation.relation_type === relationFilter.value)
    .slice(0, 40),
)
const recentEvents = computed(() => events.value.slice(0, 16))

function relationLabel(type: string): string {
  switch (type) {
    case 'updates': return t('intelligence.updates')
    case 'confirms': return t('intelligence.confirms')
    case 'contradicts': return t('intelligence.contradicts')
    case 'reaction_to': return t('intelligence.reactionTo')
    case 'continues': return t('intelligence.continues')
    default: return t('intelligence.relatedTo')
  }
}

function contextFor(id: string): IntelligenceEventContext | null {
  return eventById.value.get(id) ?? null
}

function senderLabel(context: IntelligenceEventContext): string {
  if (context.sender_username) return `@${context.sender_username}`
  return context.sender_name?.trim() || t('intelligence.unknownSender')
}

function sourceLine(context: IntelligenceEventContext): string {
  return t('intelligence.sourceSender', {
    source: context.channel_name,
    sender: senderLabel(context),
  })
}

function entityLine(context: IntelligenceEventContext): string {
  const values = context.event.entities.map((entity) => entity.name)
  if (context.event.speaker_name && !values.includes(context.event.speaker_name)) {
    values.unshift(context.event.speaker_name)
  }
  return values.slice(0, 5).join(' · ')
}

function impactLine(context: IntelligenceEventContext): string {
  const impact = context.event.impact_hypotheses[0]
  if (!impact) return ''
  const horizon = impact.horizon ? ` · ${impact.horizon}` : ''
  return `${impact.market} · ${impact.direction}${horizon} · ${(impact.confidence * 100).toFixed(0)}%`
}

function marketImpactFor(context: IntelligenceEventContext, hypothesisIndex = 0): MarketImpactEvidence | null {
  return marketImpactByEvent.value.get(context.event.id)?.find((item) => item.hypothesis_index === hypothesisIndex) ?? null
}

function marketImpactStatusLabel(evidence: MarketImpactEvidence): string {
  switch (evidence.status) {
    case 'verified': return t('intelligence.impactVerified')
    case 'pending_quotes': return t('intelligence.impactPending')
    case 'insufficient_quote_coverage': return t('intelligence.impactInsufficientCoverage')
    case 'unresolved_asset': return t('intelligence.impactUnresolvedAsset')
    case 'unresolved_horizon': return t('intelligence.impactUnresolvedHorizon')
    default: return evidence.status
  }
}

function directionEvidenceLabel(evidence: MarketImpactEvidence): string {
  if (evidence.direction_matched === true) return t('intelligence.directionMatched')
  if (evidence.direction_matched === false) return t('intelligence.directionMissed')
  return t('intelligence.directionNotScored')
}

function signedPercent(value: number): string {
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${value.toFixed(2)}%`
}

function marketImpactEvidenceLine(evidence: MarketImpactEvidence): string {
  const parts = [marketImpactStatusLabel(evidence)]
  const asset = evidence.asset_symbol?.trim() || evidence.market.trim()
  if (asset) parts.push(asset)
  if (evidence.return_pct !== null) parts.push(signedPercent(evidence.return_pct))
  if (evidence.status === 'verified') parts.push(directionEvidenceLabel(evidence))
  return parts.join(' · ')
}

function marketImpactClass(evidence: MarketImpactEvidence): string {
  return `tm-intel-verification--${evidence.status.replaceAll('_', '-')}`
}

function relationConfidence(relation: IntelligenceEventRelation): string {
  return `${(relation.confidence * 100).toFixed(0)}%`
}
</script>

<template>
  <section class="tm-intel-graph">
    <div class="tm-intel-heading">
      <div>
        <span class="tm-intel-kicker"><Network :size="15" />{{ t('intelligence.kicker') }}</span>
        <h2>{{ t('intelligence.title') }}</h2>
        <p>{{ t('intelligence.description') }}</p>
      </div>
      <span v-if="graph" class="tm-intel-generated">{{ formatLocalDateTime(graph.generated_at) }}</span>
    </div>

    <div v-if="loading && !graph" class="tm-intel-loading">
      <BrainCircuit :size="18" />{{ t('intelligence.loading') }}
    </div>

    <template v-else-if="graph">
      <div class="tm-intel-stats">
        <div><GitBranch :size="16" /><span>{{ t('intelligence.events') }}</span><strong>{{ events.length }}</strong></div>
        <div><Network :size="16" /><span>{{ t('intelligence.relations') }}</span><strong>{{ relations.length }}</strong></div>
        <div><RadioTower :size="16" /><span>{{ t('intelligence.sources') }}</span><strong>{{ sourceCount }}</strong></div>
        <div><UsersRound :size="16" /><span>{{ t('intelligence.actors') }}</span><strong>{{ entityCount }}</strong></div>
      </div>

      <article class="tm-card tm-intel-card">
        <header class="tm-intel-toolbar">
          <div class="tm-intel-filters">
            <button
              type="button"
              :class="{ active: relationFilter === 'all' }"
              @click="relationFilter = 'all'"
            >
              {{ t('intelligence.allRelations') }} <small>{{ relations.length }}</small>
            </button>
            <button
              v-for="([type, count]) in relationTypes"
              :key="type"
              type="button"
              :class="[`tm-intel-filter--${type}`, { active: relationFilter === type }]"
              @click="relationFilter = type"
            >
              {{ relationLabel(type) }} <small>{{ count }}</small>
            </button>
          </div>
        </header>

        <div v-if="filteredRelations.length" class="tm-intel-relation-list">
          <div
            v-for="relation in filteredRelations"
            :key="`${relation.subject_event_id}-${relation.object_event_id}-${relation.relation_type}`"
            class="tm-intel-relation-row"
          >
            <template v-if="contextFor(relation.object_event_id) && contextFor(relation.subject_event_id)">
              <section class="tm-intel-event tm-intel-event--older">
                <div class="tm-intel-event-meta">
                  <span>{{ t('intelligence.olderEvent') }}</span>
                  <time>{{ formatLocalDateTime(contextFor(relation.object_event_id)!.event.observed_at) }}</time>
                </div>
                <ExpandableText class="tm-intel-event-title" :text="contextFor(relation.object_event_id)!.event.headline" :lines="1" />
                <small>{{ sourceLine(contextFor(relation.object_event_id)!) }}</small>
                <ExpandableText class="tm-intel-event-summary" :text="contextFor(relation.object_event_id)!.event.summary" :lines="2" />
                <div class="tm-intel-tags">
                  <span v-if="entityLine(contextFor(relation.object_event_id)!)">{{ entityLine(contextFor(relation.object_event_id)!) }}</span>
                  <span v-for="theme in contextFor(relation.object_event_id)!.event.themes.slice(0, 3)" :key="theme">{{ theme }}</span>
                </div>
                <small v-if="impactLine(contextFor(relation.object_event_id)!)" class="tm-intel-impact">
                  {{ t('intelligence.impact') }} · {{ impactLine(contextFor(relation.object_event_id)!) }}
                </small>
                <div
                  v-if="marketImpactFor(contextFor(relation.object_event_id)!)"
                  class="tm-intel-verification"
                  :class="marketImpactClass(marketImpactFor(contextFor(relation.object_event_id)!)!)"
                  :title="marketImpactFor(contextFor(relation.object_event_id)!)!.evidence_reason"
                >
                  <strong>{{ marketImpactEvidenceLine(marketImpactFor(contextFor(relation.object_event_id)!)!) }}</strong>
                  <span v-if="marketImpactFor(contextFor(relation.object_event_id)!)!.status === 'verified'">{{ t('intelligence.associationOnly') }}</span>
                </div>
              </section>

              <section class="tm-intel-edge" :class="`tm-intel-edge--${relation.relation_type}`">
                <ArrowRight :size="17" />
                <strong>{{ relationLabel(relation.relation_type) }}</strong>
                <span>{{ relationConfidence(relation) }}</span>
                <ExpandableText class="tm-intel-edge-rationale" :text="relation.rationale" :lines="2" />
              </section>

              <section class="tm-intel-event tm-intel-event--newer">
                <div class="tm-intel-event-meta">
                  <span>{{ t('intelligence.newerEvent') }}</span>
                  <time>{{ formatLocalDateTime(contextFor(relation.subject_event_id)!.event.observed_at) }}</time>
                </div>
                <ExpandableText class="tm-intel-event-title" :text="contextFor(relation.subject_event_id)!.event.headline" :lines="1" />
                <small>{{ sourceLine(contextFor(relation.subject_event_id)!) }}</small>
                <ExpandableText class="tm-intel-event-summary" :text="contextFor(relation.subject_event_id)!.event.summary" :lines="2" />
                <div class="tm-intel-tags">
                  <span v-if="entityLine(contextFor(relation.subject_event_id)!)">{{ entityLine(contextFor(relation.subject_event_id)!) }}</span>
                  <span v-for="theme in contextFor(relation.subject_event_id)!.event.themes.slice(0, 3)" :key="theme">{{ theme }}</span>
                </div>
                <small v-if="impactLine(contextFor(relation.subject_event_id)!)" class="tm-intel-impact">
                  {{ t('intelligence.impact') }} · {{ impactLine(contextFor(relation.subject_event_id)!) }}
                </small>
                <div
                  v-if="marketImpactFor(contextFor(relation.subject_event_id)!)"
                  class="tm-intel-verification"
                  :class="marketImpactClass(marketImpactFor(contextFor(relation.subject_event_id)!)!)"
                  :title="marketImpactFor(contextFor(relation.subject_event_id)!)!.evidence_reason"
                >
                  <strong>{{ marketImpactEvidenceLine(marketImpactFor(contextFor(relation.subject_event_id)!)!) }}</strong>
                  <span v-if="marketImpactFor(contextFor(relation.subject_event_id)!)!.status === 'verified'">{{ t('intelligence.associationOnly') }}</span>
                </div>
              </section>
            </template>
          </div>
        </div>

        <div v-else-if="events.length" class="tm-intel-event-grid">
          <article v-for="context in recentEvents" :key="context.event.id" class="tm-intel-event tm-intel-event--solo">
            <div class="tm-intel-event-meta"><span>{{ context.event.event_type }}</span><time>{{ formatLocalDateTime(context.event.observed_at) }}</time></div>
            <ExpandableText class="tm-intel-event-title" :text="context.event.headline" :lines="1" />
            <small>{{ sourceLine(context) }}</small>
            <ExpandableText class="tm-intel-event-summary" :text="context.event.summary" :lines="2" />
            <div class="tm-intel-tags">
              <span v-if="entityLine(context)">{{ entityLine(context) }}</span>
              <span v-for="theme in context.event.themes.slice(0, 3)" :key="theme">{{ theme }}</span>
            </div>
            <small v-if="impactLine(context)" class="tm-intel-impact">
              {{ t('intelligence.impact') }} · {{ impactLine(context) }}
            </small>
            <div
              v-if="marketImpactFor(context)"
              class="tm-intel-verification"
              :class="marketImpactClass(marketImpactFor(context)!)"
              :title="marketImpactFor(context)!.evidence_reason"
            >
              <strong>{{ marketImpactEvidenceLine(marketImpactFor(context)!) }}</strong>
              <span v-if="marketImpactFor(context)!.status === 'verified'">{{ t('intelligence.associationOnly') }}</span>
            </div>
          </article>
          <p class="tm-intel-empty-copy">{{ t('intelligence.noRelations') }}</p>
        </div>
        <div v-else class="tm-intel-empty-copy">{{ t('intelligence.noEvents') }}</div>
      </article>
    </template>
  </section>
</template>

<style scoped>
.tm-intel-graph { display: grid; gap: 12px; min-width: 0; }
.tm-intel-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; min-width: 0; }
.tm-intel-heading > div { min-width: 0; }
.tm-intel-kicker { display: inline-flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 800; letter-spacing: .09em; opacity: .72; }
.tm-intel-heading h2 { margin: 4px 0 2px; font-size: 20px; line-height: 1.2; }
.tm-intel-heading p { margin: 0; max-width: 820px; font-size: 13px; line-height: 1.55; opacity: .72; }
.tm-intel-generated { flex: 0 0 auto; white-space: nowrap; font-size: 11px; opacity: .62; }
.tm-intel-loading { display: flex; align-items: center; gap: 8px; min-height: 80px; justify-content: center; font-size: 13px; opacity: .72; }
.tm-intel-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.tm-intel-stats > div { display: grid; grid-template-columns: 20px 1fr auto; align-items: center; gap: 7px; min-width: 0; padding: 9px 11px; border: 1px solid var(--tm-border, rgba(148,163,184,.18)); border-radius: 10px; background: rgba(15,23,42,.34); }
.tm-intel-stats span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; opacity: .66; }
.tm-intel-stats strong { font-size: 17px; font-variant-numeric: tabular-nums; }
.tm-intel-card { min-width: 0; padding: 10px; overflow: hidden; }
.tm-intel-toolbar { min-width: 0; margin-bottom: 8px; }
.tm-intel-filters { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.tm-intel-filters button { display: inline-flex; align-items: center; gap: 5px; min-width: 0; max-width: 180px; padding: 5px 8px; border: 1px solid rgba(148,163,184,.18); border-radius: 8px; background: rgba(15,23,42,.28); color: inherit; font: inherit; font-size: 11px; cursor: pointer; }
.tm-intel-filters button.active { border-color: rgba(99,102,241,.52); background: rgba(99,102,241,.14); }
.tm-intel-filters small { opacity: .55; font-variant-numeric: tabular-nums; }
.tm-intel-relation-list { display: grid; gap: 7px; min-width: 0; }
.tm-intel-relation-row { display: grid; grid-template-columns: minmax(0, 1fr) 118px minmax(0, 1fr); align-items: stretch; gap: 8px; min-width: 0; }
.tm-intel-event { display: grid; align-content: start; gap: 5px; min-width: 0; padding: 9px 10px; border: 1px solid rgba(148,163,184,.14); border-radius: 9px; background: rgba(15,23,42,.24); overflow: hidden; }
.tm-intel-event--newer { background: rgba(79,70,229,.055); border-color: rgba(99,102,241,.19); }
.tm-intel-event-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; font-size: 10px; opacity: .58; }
.tm-intel-event-meta span, .tm-intel-event-meta time { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tm-intel-event-title { color: inherit; font-size: 13px; font-weight: 700; line-height: 1.35; }
.tm-intel-event > small { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; opacity: .62; }
.tm-intel-event-summary { margin: 0; min-width: 0; font-size: 11px; line-height: 1.45; opacity: .78; overflow-wrap: anywhere; }
.tm-intel-tags { display: flex; flex-wrap: wrap; gap: 4px; min-width: 0; }
.tm-intel-tags span { max-width: 100%; padding: 2px 5px; border-radius: 5px; background: rgba(148,163,184,.08); font-size: 9px; opacity: .72; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tm-intel-impact { color: #a5b4fc; opacity: .86 !important; }
.tm-intel-verification { display: grid; gap: 2px; min-width: 0; margin-top: 1px; padding: 5px 7px; border: 1px solid rgba(148,163,184,.14); border-radius: 7px; background: rgba(148,163,184,.055); }
.tm-intel-verification strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; line-height: 1.35; }
.tm-intel-verification span { font-size: 9px; line-height: 1.35; opacity: .66; }
.tm-intel-verification--verified { border-color: rgba(16,185,129,.24); background: rgba(16,185,129,.075); }
.tm-intel-verification--pending-quotes { border-color: rgba(59,130,246,.2); background: rgba(59,130,246,.06); }
.tm-intel-verification--insufficient-quote-coverage,
.tm-intel-verification--unresolved-asset,
.tm-intel-verification--unresolved-horizon { border-color: rgba(245,158,11,.22); background: rgba(245,158,11,.065); }
.tm-intel-edge { display: grid; align-content: center; justify-items: center; gap: 3px; min-width: 0; padding: 8px 5px; border-radius: 9px; background: rgba(148,163,184,.055); text-align: center; }
.tm-intel-edge svg { opacity: .58; }
.tm-intel-edge strong { max-width: 100%; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tm-intel-edge > span { font-size: 11px; font-weight: 800; font-variant-numeric: tabular-nums; }
.tm-intel-edge-rationale { max-width: 100%; font-size: 9px; line-height: 1.35; opacity: .58; overflow-wrap: anywhere; }
.tm-intel-edge--confirms { background: rgba(16,185,129,.08); }
.tm-intel-edge--contradicts { background: rgba(239,68,68,.09); }
.tm-intel-edge--reaction_to { background: rgba(245,158,11,.08); }
.tm-intel-edge--updates { background: rgba(59,130,246,.08); }
.tm-intel-event-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; min-width: 0; }
.tm-intel-event--solo { min-height: 0; }
.tm-intel-empty-copy { grid-column: 1 / -1; margin: 0; padding: 16px 10px; text-align: center; font-size: 12px; opacity: .6; }

@media (max-width: 1100px) {
  .tm-intel-relation-row { grid-template-columns: minmax(0, 1fr) 96px minmax(0, 1fr); }
}
@media (max-width: 900px) {
  .tm-intel-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tm-intel-relation-row { grid-template-columns: minmax(0, 1fr); }
  .tm-intel-edge { grid-template-columns: auto auto auto; justify-content: start; justify-items: start; text-align: left; padding: 6px 9px; }
  .tm-intel-edge svg { transform: rotate(90deg); }
  .tm-intel-edge small { grid-column: 1 / -1; }
}
@media (max-width: 620px) {
  .tm-intel-heading { align-items: flex-start; flex-direction: column; gap: 5px; }
  .tm-intel-stats, .tm-intel-event-grid { grid-template-columns: minmax(0, 1fr); }
  .tm-intel-generated { white-space: normal; }
}
</style>
